// src/pages/CookiesPolicy.jsx
import React, { useEffect, useState } from "react";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";

  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return ["es", "fr"].includes(savedLanguage) ? savedLanguage : "es";
}

const COOKIES_COPY = {
  es: {
    title: "Política de Cookies",
    updated: "Última actualización: junio de 2026",
    intro: (
      <>
        Esta Política explica cómo <strong>ZEDRO Academy</strong>, plataforma
        operada bajo el nombre comercial registrado <strong>Académie ZEDRO</strong>{" "}
        en Québec, Canadá, utiliza cookies y tecnologías similares en{" "}
        <strong>zedro.academy</strong> y en los servicios digitales relacionados.
      </>
    ),
    sections: [
      {
        title: "1. Qué son las cookies",
        content: (
          <>
            Las cookies son pequeños archivos que se almacenan en tu navegador o
            dispositivo cuando visitas un sitio web. También podemos utilizar
            tecnologías similares, como almacenamiento local del navegador,
            identificadores técnicos o herramientas de medición, para recordar
            preferencias, mantener sesiones, mejorar la seguridad y comprender
            cómo se utiliza la plataforma.
          </>
        ),
      },
      {
        title: "2. Tipos de cookies y tecnologías que podemos utilizar",
        content: (
          <ul>
            <li>
              <strong>Cookies esenciales:</strong> necesarias para el
              funcionamiento básico del sitio, inicio de sesión, seguridad,
              navegación, acceso a la cuenta, gestión de membresía y proceso de
              compra.
            </li>
            <li>
              <strong>Cookies de preferencias:</strong> permiten recordar
              configuraciones como idioma, consentimiento de cookies u otras
              preferencias de experiencia.
            </li>
            <li>
              <strong>Cookies de analítica:</strong> nos ayudan a comprender el
              uso general del sitio, rendimiento, errores, páginas visitadas y
              experiencia de usuario, con el fin de mejorar ZEDRO Academy.
            </li>
            <li>
              <strong>Cookies de pago o terceros:</strong> proveedores como
              Stripe pueden utilizar cookies o tecnologías similares para
              procesar pagos, prevenir fraude, proteger transacciones y cumplir
              sus propias obligaciones legales y de seguridad.
            </li>
            <li>
              <strong>Publicidad o segmentación:</strong> actualmente no son el
              centro de ZEDRO Academy. Si en el futuro se habilitan herramientas
              de publicidad, medición avanzada o segmentación, se solicitará el
              consentimiento correspondiente cuando la ley aplicable lo exija.
            </li>
          </ul>
        ),
      },
      {
        title: "3. Para qué utilizamos cookies",
        content: (
          <ul>
            <li>mantener la seguridad y funcionamiento de la plataforma;</li>
            <li>permitir el inicio de sesión y acceso a la cuenta;</li>
            <li>recordar preferencias como el idioma seleccionado;</li>
            <li>gestionar el consentimiento de cookies;</li>
            <li>facilitar procesos de pago y membresía mediante Stripe;</li>
            <li>medir rendimiento, errores y uso general del sitio;</li>
            <li>mejorar la experiencia, navegación y claridad del servicio.</li>
          </ul>
        ),
      },
      {
        title: "4. Cookies esenciales y no esenciales",
        content: (
          <>
            Algunas cookies son esenciales para que el sitio funcione
            correctamente y no pueden desactivarse desde nuestros sistemas sin
            afectar la operación de la plataforma. Las cookies no esenciales,
            como ciertas cookies de analítica o publicidad, se utilizarán conforme
            al consentimiento del usuario cuando la ley aplicable lo requiera.
          </>
        ),
      },
      {
        title: "5. Consentimiento y gestión de preferencias",
        content: (
          <>
            Cuando sea necesario, ZEDRO Academy solicitará tu consentimiento para
            el uso de cookies no esenciales. Puedes aceptar, rechazar o modificar
            tus preferencias desde el banner o panel de cookies de la plataforma,
            cuando esté disponible. También puedes administrar cookies desde la
            configuración de tu navegador.
          </>
        ),
      },
      {
        title: "6. Cómo administrar cookies desde tu navegador",
        content: (
          <>
            La mayoría de navegadores permiten bloquear, eliminar o limitar el
            uso de cookies. Si desactivas ciertas cookies, algunas funciones de
            ZEDRO Academy pueden no operar correctamente, especialmente las
            relacionadas con sesión, cuenta, idioma, seguridad, compra o acceso a
            la membresía.
          </>
        ),
      },
      {
        title: "7. Servicios de terceros",
        content: (
          <>
            Algunos proveedores externos pueden utilizar cookies o tecnologías
            similares en relación con sus servicios. Esto puede incluir, entre
            otros, Stripe para pagos, proveedores de infraestructura, alojamiento,
            correo electrónico, seguridad o herramientas de analítica. Estos
            terceros operan conforme a sus propias políticas de privacidad,
            cookies y seguridad.
          </>
        ),
      },
      {
        title: "8. Actualizaciones de esta Política",
        content: (
          <>
            Podemos actualizar esta Política de Cookies para reflejar cambios en
            nuestras prácticas, herramientas tecnológicas, proveedores,
            obligaciones legales o funcionamiento de la plataforma. La versión
            vigente será la publicada en ZEDRO Academy.
          </>
        ),
      },
      {
        title: "9. Contacto",
        content: (
          <>
            Para preguntas sobre esta Política de Cookies, privacidad o gestión
            de datos, puedes escribir a{" "}
            <a href="mailto:admin@zedro.academy">admin@zedro.academy</a>. Para
            soporte relacionado con cuenta, acceso, pago o cancelación, puedes
            escribir a{" "}
            <a href="mailto:support@zedro.academy">support@zedro.academy</a>.
          </>
        ),
      },
      {
        title: "10. Relación con otros documentos legales",
        content: (
          <>
            Esta Política de Cookies complementa la{" "}
            <a href="/legal/privacidad">Política de Privacidad</a>, los{" "}
            <a href="/legal/terminos">Términos y Condiciones</a>, el{" "}
            <a href="/legal/aviso-legal">Aviso Legal</a> y el{" "}
            <a href="/legal/descargo">Descargo de Responsabilidad</a> de ZEDRO
            Academy.
          </>
        ),
      },
    ],
  },
  fr: {
    title: "Politique de cookies",
    updated: "Dernière mise à jour : juin 2026",
    intro: (
      <>
        La présente Politique explique comment <strong>ZEDRO Academy</strong>,
        plateforme exploitée sous le nom commercial enregistré{" "}
        <strong>Académie ZEDRO</strong> au Québec, Canada, utilise des cookies et
        des technologies similaires sur <strong>zedro.academy</strong> et dans
        les services numériques connexes.
      </>
    ),
    sections: [
      {
        title: "1. Que sont les cookies",
        content: (
          <>
            Les cookies sont de petits fichiers enregistrés dans votre navigateur
            ou sur votre appareil lorsque vous visitez un site web. Nous pouvons
            également utiliser des technologies similaires, comme le stockage
            local du navigateur, des identifiants techniques ou des outils de
            mesure, afin de mémoriser des préférences, maintenir des sessions,
            améliorer la sécurité et comprendre comment la plateforme est
            utilisée.
          </>
        ),
      },
      {
        title: "2. Types de cookies et de technologies que nous pouvons utiliser",
        content: (
          <ul>
            <li>
              <strong>Cookies essentiels :</strong> nécessaires au
              fonctionnement de base du site, à la connexion, à la sécurité, à la
              navigation, à l’accès au compte, à la gestion de l’abonnement et au
              processus d’achat.
            </li>
            <li>
              <strong>Cookies de préférences :</strong> permettent de mémoriser
              des réglages tels que la langue, le consentement aux cookies ou
              d’autres préférences d’expérience.
            </li>
            <li>
              <strong>Cookies d’analyse :</strong> nous aident à comprendre
              l’utilisation générale du site, la performance, les erreurs, les
              pages consultées et l’expérience utilisateur, afin d’améliorer
              ZEDRO Academy.
            </li>
            <li>
              <strong>Cookies de paiement ou de tiers :</strong> des fournisseurs
              comme Stripe peuvent utiliser des cookies ou technologies
              similaires pour traiter les paiements, prévenir la fraude, protéger
              les transactions et respecter leurs propres obligations légales et
              de sécurité.
            </li>
            <li>
              <strong>Publicité ou ciblage :</strong> ces outils ne sont pas au
              cœur de ZEDRO Academy actuellement. Si des outils de publicité, de
              mesure avancée ou de ciblage sont activés à l’avenir, le
              consentement approprié sera demandé lorsque la loi applicable
              l’exige.
            </li>
          </ul>
        ),
      },
      {
        title: "3. Pourquoi nous utilisons des cookies",
        content: (
          <ul>
            <li>maintenir la sécurité et le fonctionnement de la plateforme;</li>
            <li>permettre la connexion et l’accès au compte;</li>
            <li>mémoriser des préférences comme la langue sélectionnée;</li>
            <li>gérer le consentement aux cookies;</li>
            <li>
              faciliter les processus de paiement et d’abonnement par Stripe;
            </li>
            <li>
              mesurer la performance, les erreurs et l’utilisation générale du
              site;
            </li>
            <li>
              améliorer l’expérience, la navigation et la clarté du service.
            </li>
          </ul>
        ),
      },
      {
        title: "4. Cookies essentiels et non essentiels",
        content: (
          <>
            Certains cookies sont essentiels au bon fonctionnement du site et ne
            peuvent pas être désactivés dans nos systèmes sans affecter le
            fonctionnement de la plateforme. Les cookies non essentiels, comme
            certains cookies d’analyse ou de publicité, seront utilisés
            conformément au consentement de l’utilisateur lorsque la loi
            applicable l’exige.
          </>
        ),
      },
      {
        title: "5. Consentement et gestion des préférences",
        content: (
          <>
            Lorsque cela est nécessaire, ZEDRO Academy demandera votre
            consentement pour l’utilisation de cookies non essentiels. Vous
            pouvez accepter, refuser ou modifier vos préférences depuis la
            bannière ou le panneau de cookies de la plateforme, lorsqu’il est
            disponible. Vous pouvez également gérer les cookies depuis les
            paramètres de votre navigateur.
          </>
        ),
      },
      {
        title: "6. Gérer les cookies depuis votre navigateur",
        content: (
          <>
            La plupart des navigateurs permettent de bloquer, supprimer ou
            limiter l’utilisation des cookies. Si vous désactivez certains
            cookies, certaines fonctions de ZEDRO Academy peuvent ne pas
            fonctionner correctement, notamment celles liées à la session, au
            compte, à la langue, à la sécurité, à l’achat ou à l’accès à
            l’abonnement.
          </>
        ),
      },
      {
        title: "7. Services de tiers",
        content: (
          <>
            Certains fournisseurs externes peuvent utiliser des cookies ou des
            technologies similaires dans le cadre de leurs services. Cela peut
            inclure notamment Stripe pour les paiements, des fournisseurs
            d’infrastructure, d’hébergement, de courriel, de sécurité ou des
            outils d’analyse. Ces tiers fonctionnent selon leurs propres
            politiques de confidentialité, de cookies et de sécurité.
          </>
        ),
      },
      {
        title: "8. Mises à jour de la présente Politique",
        content: (
          <>
            Nous pouvons mettre à jour la présente Politique de cookies afin de
            refléter des changements dans nos pratiques, nos outils
            technologiques, nos fournisseurs, nos obligations légales ou le
            fonctionnement de la plateforme. La version en vigueur sera celle
            publiée sur ZEDRO Academy.
          </>
        ),
      },
      {
        title: "9. Contact",
        content: (
          <>
            Pour toute question concernant la présente Politique de cookies, la
            confidentialité ou la gestion des données, vous pouvez écrire à{" "}
            <a href="mailto:admin@zedro.academy">admin@zedro.academy</a>. Pour
            toute assistance liée au compte, à l’accès, au paiement ou à
            l’annulation, vous pouvez écrire à{" "}
            <a href="mailto:support@zedro.academy">support@zedro.academy</a>.
          </>
        ),
      },
      {
        title: "10. Lien avec les autres documents juridiques",
        content: (
          <>
            La présente Politique de cookies complète la{" "}
            <a href="/legal/privacidad">Politique de confidentialité</a>, les{" "}
            <a href="/legal/terminos">Conditions générales</a>, l’{" "}
            <a href="/legal/aviso-legal">Avis légal</a> et l’{" "}
            <a href="/legal/descargo">Avis de non-responsabilité</a> de ZEDRO
            Academy.
          </>
        ),
      },
    ],
  },
};

export default function CookiesPolicy() {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    if (typeof window === "undefined") return;

    function syncLanguage(nextLanguage) {
      if (["es", "fr"].includes(nextLanguage)) {
        setLanguage(nextLanguage);
      } else {
        setLanguage("es");
      }
    }

    function handleLanguageEvent(event) {
      syncLanguage(event?.detail?.language);
    }

    function handleStorage(event) {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        syncLanguage(event.newValue);
      }
    }

    syncLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
    window.addEventListener("zedro:languagechange", handleLanguageEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("zedro:languagechange", handleLanguageEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const copy = COOKIES_COPY[language] || COOKIES_COPY.es;

  return (
    <main
      style={{
        maxWidth: 900,
        margin: "2rem auto",
        padding: "0 1rem",
        fontFamily: "Questrial, sans-serif",
        lineHeight: 1.75,
        color: "#2c3b51",
      }}
    >
      <h1>{copy.title}</h1>

      <p
        style={{
          color: "rgba(44, 59, 81, 0.72)",
          fontSize: "0.95rem",
          marginTop: "-0.4rem",
        }}
      >
        {copy.updated}
      </p>

      <p style={{ marginTop: 0 }}>{copy.intro}</p>

      {copy.sections.map((section) => (
        <section key={section.title} style={{ marginTop: "1.5rem" }}>
          <h2
            style={{
              color: "#286b94",
              fontSize: "1.2rem",
              marginBottom: "0.45rem",
            }}
          >
            {section.title}
          </h2>

          <div>{section.content}</div>
        </section>
      ))}
    </main>
  );
}